import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { BUTTON_LABELS } from "@miplatform/constants";
import { useRouter } from "@miplatform/hooks";
import { zodResolver } from "@hookform/resolvers/zod";
import { VerticalTabsLayout } from "@mibuildweb/components";
import { useTheme } from "@mui/material";
import { DropTypeDetailsTab } from "./tabs/DropTypeDetailsTab/DropTypeDetailsTab";
import { ConfigPageLayout } from "../../../../Config/ConfigPageLayout";
import { IDropTypeData } from "../DropTypes.types";
import { useAddDropTypeViewModel } from "./useAddDropTypeViewModel";

const addDropTypeSchema = z.object({
  name: z.string().min(1, "Drop Type name is required."),
});

export function AddDropType() {
  const { params, goBack } = useRouter();
  const { id: dropTypeId } = params;
  const theme = useTheme();

  const form = useForm<IDropTypeData>({
    mode: "onChange",
    defaultValues: {
      name: "",
    },
    resolver: zodResolver(addDropTypeSchema),
  });

  const {
    formState: { isValid, isDirty },
    reset,
    getValues,
    watch,
  } = form;

  const { name } = watch();

  const {
    isLoading,
    fetchDropTypeDetails,
    handleCreateDropType,
    handleUpdateDropType,
  } = useAddDropTypeViewModel({
    goBack,
    reset,
  });

  useEffect(() => {
    if (dropTypeId) {
      fetchDropTypeDetails(dropTypeId);
    }
  }, []);

  const tabs = useMemo(
    () => [
      {
        label: "Details",
        value: "details",
        controlledFormViewComponent: DropTypeDetailsTab,
        isComplete: isValid,
        hasErrors: !isValid,
      },
    ],
    [isValid]
  );

  const handleSubmit = () => {
    const data = getValues();
    if (dropTypeId) {
      handleUpdateDropType(data);
    } else {
      handleCreateDropType(data);
    }
  };

  const renderTitle = () => {
    const action = dropTypeId ? "Edit" : "Add";
    return (
      <>
        {action} Drop Type:{" "}
        <span style={{ color: theme.palette.primary.main }}>{name}</span>
      </>
    );
  };

  const actions = {
    cancel: {
      label: BUTTON_LABELS.CANCEL,
      onClick: () => {
        goBack();
      },
    },
    submit: {
      label: dropTypeId ? "Update Drop Type" : "Save Drop Type",
      onClick: () => {
        handleSubmit();
      },
      disabled: !isDirty || !isValid || isLoading,
    },
  };

  return (
    <ConfigPageLayout
      renderTitle={renderTitle}
      bottomActions={actions}
      loading={isLoading}
    >
      <VerticalTabsLayout
        tabs={tabs}
        form={form}
        transparentBackground
        hideNavigationButtons
      />
    </ConfigPageLayout>
  );
}
